import styled from 'styled-components';
import { Clef } from '../../../../../common/src/learn_rules.ts';
import { PitchDotColor, pitchDotColorCss, pitchLockedGreen } from './pitch_colors.ts';
import {
  captionYFor,
  dotStep,
  ledgerStepsFor,
  staffLayout,
  StaffLetterKind,
  staffLetters,
  staffStepForMidi,
  stemDirection,
  stepY,
} from './staff_geometry.ts';

// The staff Pitch Match draws (issue #3518), from the "Pitch Match, staff version" artboards: five
// lines with no clef, the line letters grey on white tabs at the left and the space letters in the
// accent purple beside them, the target as a filled notehead with a stem and a dashed guide line
// running right from it, the singer's dot in the "you" column with its halo, and the two captions.
// A target beyond the staff gets short ledger lines centred on its notehead, with their letters
// beside them. Every number comes from staff_geometry.ts, so this file only turns geometry into
// SVG; it holds no state and knows nothing about audio. On a lock the target, the guide line and
// the dot all turn green together.

export const pitchStaffTestId = 'pitch-staff';
export const pitchTargetTestId = 'pitch-target';
export const pitchDotTestId = 'pitch-dot';
export const pitchLetterTestId = 'pitch-letter';
export const pitchLedgerTestId = 'pitch-ledger';

// A 14 px letter sits on its line or space when its baseline is this far below it (the artboard's
// numbers: a letter on the top line at y 110 has its baseline at 115)
const letterBaselineOffset = 5;

const semiBoldFont = 'Jost-SemiBold, Arial, sans-serif';
const regularFont = 'Jost-Regular, Arial, sans-serif';

const staffLineColor = 'var(--color-text)';
const lineLetterColor = 'var(--color-text-darker)';
const spaceLetterColor = 'var(--color-tab-selected)';
// The page behind the staff (the artboard's white): the letter tabs and the dot's ring are cut out of
// it, so both read over a staff line in either theme
const paperColor = 'var(--color-background)';
const targetPurple = 'var(--color-tab-selected)';

const lineSteps = [0, 2, 4, 6, 8];

// The five line letters and the four space letters keep their columns at the left of the staff; a
// ledger letter, and the space letter of a target outside the staff, travel with the note instead -
// beside its short ledger lines, where a musician reads them.
function letterX(step: number, kind: StaffLetterKind): number {
  if (kind === 'ledger' || step < 0 || step > 8) {
    return staffLayout.ledgerLetterX;
  }
  return kind === 'line' ? staffLayout.lineLetterX : staffLayout.spaceLetterX;
}

// `dot` is the state machine's dotFor(): the singer's distance from the target in cents and the
// colour it earns. Nothing is drawn in the "you" column while it is undefined (silence, noise, or
// a missed note). `locked` turns the whole picture green.
export const PitchStaff = ({
  clef,
  targetMidi,
  dot,
  locked,
}: {
  clef: Clef;
  targetMidi: number;
  dot?: { cents: number; color: PitchDotColor };
  locked: boolean;
}) => {
  const targetStep = staffStepForMidi(targetMidi, clef);
  const targetY = stepY(targetStep, clef);
  const captionY = captionYFor(targetStep, clef);
  const stem = stemDirection(targetStep);
  const stemX = staffLayout.targetX + (stem === 'up' ? staffLayout.stemOffsetX : -staffLayout.stemOffsetX);
  const targetColor = locked ? pitchLockedGreen : targetPurple;
  const dotColor = locked || !dot ? pitchLockedGreen : pitchDotColorCss[dot.color];
  return (
    <Svg
      data-testid={pitchStaffTestId}
      data-clef={clef}
      data-target-step={targetStep}
      data-locked={locked ? 'true' : 'false'}
      viewBox={`0 0 ${staffLayout.width} ${staffLayout.height}`}
    >
      {lineSteps.map((step) => (
        <line
          key={step}
          x1={staffLayout.lineX1}
          y1={stepY(step, clef)}
          x2={staffLayout.lineX2}
          y2={stepY(step, clef)}
          stroke={staffLineColor}
          strokeWidth={staffLayout.lineWidth}
        />
      ))}
      {/* Each ledger line is a short line centred on the notehead, as printed music draws it */}
      {ledgerStepsFor(targetStep).map((step) => (
        <line
          key={step}
          data-testid={pitchLedgerTestId}
          data-step={step}
          x1={staffLayout.targetX - staffLayout.ledgerHalfWidth}
          y1={stepY(step, clef)}
          x2={staffLayout.targetX + staffLayout.ledgerHalfWidth}
          y2={stepY(step, clef)}
          stroke={staffLineColor}
          strokeWidth={staffLayout.lineWidth}
        />
      ))}
      {staffLetters(clef, targetStep).map(({ step, letter, kind }) => {
        const onLine = kind !== 'space';
        const y = stepY(step, clef);
        const x = letterX(step, kind);
        return (
          <g key={`${kind}-${step}`}>
            {onLine && (
              <rect
                x={x - staffLayout.tabSize / 2}
                y={y - staffLayout.tabSize / 2}
                width={staffLayout.tabSize}
                height={staffLayout.tabSize}
                rx={staffLayout.tabCornerRadius}
                fill={paperColor}
              />
            )}
            <text
              data-testid={pitchLetterTestId}
              data-step={step}
              data-letter={letter}
              data-kind={kind}
              x={x}
              y={y + letterBaselineOffset}
              textAnchor="middle"
              fontFamily={semiBoldFont}
              fontSize={staffLayout.letterFontSize}
              fill={onLine ? lineLetterColor : spaceLetterColor}
            >
              {letter}
            </text>
          </g>
        );
      })}
      <line
        x1={staffLayout.guideX1}
        y1={targetY}
        x2={staffLayout.guideX2}
        y2={targetY}
        stroke={targetColor}
        strokeWidth={1}
        strokeDasharray={staffLayout.guideDash}
        opacity={staffLayout.guideOpacity}
      />
      <g data-testid={pitchTargetTestId} data-step={targetStep} data-stem={stem}>
        <ellipse
          cx={staffLayout.targetX}
          cy={targetY}
          rx={staffLayout.noteheadRx}
          ry={staffLayout.noteheadRy}
          transform={`rotate(${staffLayout.noteheadRotation} ${staffLayout.targetX} ${targetY})`}
          fill={targetColor}
        />
        <line
          x1={stemX}
          y1={targetY + (stem === 'up' ? -staffLayout.stemFootOffsetY : staffLayout.stemFootOffsetY)}
          x2={stemX}
          y2={targetY + (stem === 'up' ? -staffLayout.stemLength : staffLayout.stemLength)}
          stroke={targetColor}
          strokeWidth={staffLayout.stemWidth}
        />
      </g>
      {dot && (
        // Every frame moves the group to the step the cents put it on, with no CSS transition: the
        // state machine smooths the reading, and a transition retargeted each frame never completes
        // in WebKit - the dot sat at the singer's last pause while the words were live (issue #3587).
        <g
          data-testid={pitchDotTestId}
          data-color={locked ? 'green' : dot.color}
          data-cents={Math.round(dot.cents)}
          style={{ transform: `translateY(${stepY(dotStep(targetStep, dot.cents, clef), clef)}px)` }}
        >
          <circle
            cx={staffLayout.dotX}
            cy={0}
            r={staffLayout.dotRadius}
            fill={dotColor}
            stroke={paperColor}
            strokeWidth={staffLayout.dotStrokeWidth}
          />
          <circle
            cx={staffLayout.dotX}
            cy={0}
            r={staffLayout.haloRadius}
            fill="none"
            stroke={dotColor}
            strokeWidth={staffLayout.haloStrokeWidth}
            opacity={staffLayout.haloOpacity}
          />
        </g>
      )}
      <text
        x={staffLayout.targetX}
        y={captionY}
        textAnchor="middle"
        fontFamily={regularFont}
        fontSize={staffLayout.captionFontSize}
        fill={lineLetterColor}
      >
        target
      </text>
      <text
        x={staffLayout.dotX}
        y={captionY}
        textAnchor="middle"
        fontFamily={regularFont}
        fontSize={staffLayout.captionFontSize}
        fill={lineLetterColor}
      >
        you
      </text>
    </Svg>
  );
};

// The staff is drawn at the artboard's 350 units and scaled down to whatever the screen gives it,
// so it never crowds the 390 px phone the artboards are sized for.
const Svg = styled.svg`
  display: block;
  width: 100%;
  max-width: ${staffLayout.width}px;
  height: auto;
  margin: 0 auto;
`;
